import {
  collection,
  getCountFromServer,
  getDocs,
  limit,
  or,
  orderBy,
  query,
  startAfter,
  where,
} from "firebase/firestore";
import { db } from "../../Firebase/Firebase.utils";

const COLLECTION = "masterlist";

// Scope for the CMS view. Intentionally broader than the *public*
// gate: we include buildings the CMS has explicitly hidden
// (showOnWebsite === false) so ops can search for them and toggle them
// back on. Buildings that never qualified in the first place
// (no exclusive/webStatus and no showOnWebsite anywhere) stay out.
//
// The public site applies the stricter gate in
// nextjsprojv1/src/app/utils/firebase.utils.jsx.
//
// First query may prompt Firestore to build a composite index (follow
// the console link).
const publishedFilter = () =>
  or(
    where("showOnWebsite", "==", true),
    where("showOnWebsite", "==", false),
    where("exclusive", "==", "Yes"),
    where("webStatus", "==", "published"),
  );

// True iff this building would render on the public site. Useful for
// tagging hidden ones in the CMS list.
export function isPubliclyVisible(doc) {
  if (!doc) return false;
  if (doc.showOnWebsite === true) return true;
  if (doc.showOnWebsite === false) return false;
  return doc.exclusive === "Yes" || doc.webStatus === "published";
}

/**
 * Mirrors listingsQuery.js but for the `masterlist` collection (office
 * buildings). Prefix-search field is `buildingName` instead of
 * `property_name`. See listingsQuery.js for the design rationale on
 * hybrid browse/search.
 */
function buildConstraints({ search, cursor, pageSize, includeLimit }) {
  const constraints = [publishedFilter()];
  const q = (search || "").trim();

  if (q) {
    constraints.push(where("buildingName", ">=", q));
    constraints.push(where("buildingName", "<=", q + ""));
    constraints.push(orderBy("buildingName"));
  } else {
    constraints.push(orderBy("__name__"));
  }

  if (cursor) constraints.push(startAfter(cursor));
  if (includeLimit) constraints.push(limit(pageSize));

  return constraints;
}

export async function fetchBuildingsCount({ search } = {}) {
  const constraints = buildConstraints({ search, includeLimit: false });
  const q = query(collection(db, COLLECTION), ...constraints);
  const snap = await getCountFromServer(q);
  return snap.data().count;
}

export async function fetchAllBuildings() {
  const q = query(
    collection(db, COLLECTION),
    publishedFilter(),
    orderBy("__name__"),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ ...d.data(), id: d.id }));
}

export async function fetchBuildingsPage({
  pageSize = 24,
  cursor = null,
  search,
} = {}) {
  const constraints = buildConstraints({
    search,
    cursor,
    pageSize,
    includeLimit: true,
  });
  const q = query(collection(db, COLLECTION), ...constraints);
  const snap = await getDocs(q);
  return {
    docs: snap.docs.map((d) => ({ ...d.data(), id: d.id })),
    snaps: snap.docs,
  };
}
